import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { find, first, includes, map, eq, isEmpty, set, get, findIndex, uniq, filter, difference } from 'lodash';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import removeHtml from '../../util/remove_html_markup';
import { PageList } from '.';
import { Toggle } from './inputControls';
import {
  SYNC_PAGE_DATA,
  CLIENT_STATE,
  SET_CLIENT_STATE,
  REMOVE_SYNC_DATA,
  PAGE_DATA,
  UPDATE_SYNC_PAGE_DATA,
  CREATE_PUBLISH_DATA,
  LAST_SYNC_DATE,
  GENERAL_SETTINGS,
  PAGE_LIST,
} from '../../graphqls';
import { SyncPreConfirm } from './modals';
import getUserId from '../../util/getUserId';

export default function SyncPage() {
  const [showPreConfirm, setShowPreConfirm] = useState(true);
  const [overrideLabels, setOverrideLabels] = useState(false);
  const [syncType, setSyncType] = useState('CURRENT');
  const [withPublish, setWithPublish] = useState(false);
  const { loading: loadingClientState, data: { selectedPage, checked } = {} } = useQuery(CLIENT_STATE);
  const { loading: loadingLastSyncDate, data: { lastSyncDate: { createdAt } = {} } = {} } = useQuery(LAST_SYNC_DATE);
  const [setState] = useMutation(SET_CLIENT_STATE);
  const { loading: loadingSyncPageData, data: { syncPageData = [] } = {} } = useQuery(SYNC_PAGE_DATA, {
    onCompleted({ syncPageData: data }) {
      if (data.length) {
        setState({
          variables: {
            checked: data.map(({ id, status }) => ({
              id,
              current: status === 'added' ? [] : ['title', 'description', 'textContent', 'visualTitle'],
              sync: status === 'added' ? ['title', 'description', 'textContent', 'visualTitle'] : [],
            })),
            sideMenu: 'pageListView',
          },
        });
      }
    },
  });
  const [createPublishData] = useMutation(CREATE_PUBLISH_DATA, {
    onCompleted: () => {
      window.location.reload();
    },
  });
  const [removeSyncData] = useMutation(REMOVE_SYNC_DATA, {
    onCompleted: () => {
      window.location.reload();
    },
  });
  const [updateSyncPageData] = useMutation(UPDATE_SYNC_PAGE_DATA, {
    refetchQueries: [{ query: GENERAL_SETTINGS }, { query: PAGE_DATA }, { query: PAGE_LIST }],
    onCompleted: () => {
      if (withPublish) {
        const userId = getUserId(window.userId);
        if (!userId) {
          return console.error('userId should exist');
        }
        createPublishData({ variables: { userId } });
      } else {
        window.location.reload();
      }
    },
  });
  const { id, status, isMeasure, currentData, syncData } = find(syncPageData, ['id', selectedPage]) || first(syncPageData) || {};
  const checkedData = find(checked, ['id', id]) || { current: [], sync: [] };
  const textContentLabel = isMeasure ? 'Comment: ' : 'Latest Update: ';
  const disabled = !isEmpty(status);
  const onCheck = ({
    target: {
      dataset: { type, flag },
    },
  }) =>
    setState({
      variables: {
        checked: set(
          set(
            JSON.parse(JSON.stringify(checked)),
            [findIndex(checked, ['id', id]), flag],
            uniq([...get(find(checked, ['id', id]), flag), type]),
          ),
          [findIndex(checked, ['id', id]), flag === 'current' ? 'sync' : 'current'],
          difference([...get(find(checked, ['id', id]), flag === 'current' ? 'sync' : 'current')], [type]),
        ),
      },
    });
  const onCloseConfirm = () => setShowPreConfirm(false);
  const onSwitchSyncType = ({ target: { value } }) => setSyncType(value);
  const onTogglePublish = () => setWithPublish(!withPublish);
  const onToggleOverrideLabels = () => setOverrideLabels(!overrideLabels);
  const onConfirm = (event) => {
    event.preventDefault();

    const {
      target: {
        syncType: { value },
      },
    } = event;
    switch (value) {
      case 'INCOMING':
        const checkedSyncItems = syncPageData.map(({ id: itemId, status: itemStatus }) => ({
          id: itemId,
          current: itemStatus === 'added' || !itemStatus ? [] : ['title', 'description', 'textContent', 'visualTitle'],
          sync: itemStatus === 'added' || !itemStatus ? ['title', 'description', 'textContent', 'visualTitle'] : [],
        }));

        updateSyncPageData({
          variables: {
            affectedResults: JSON.stringify(
              map(
                filter(checkedSyncItems, ({ sync }) => sync.length),
                ({ id: itemId, sync }) => ({ [itemId]: sync }),
              ),
            ),
            overrideLabels,
          },
        });

        break;
      case 'CURRENT':
        const checkedCurrentItems = syncPageData.map(({ id: itemId, status: itemStatus }) => ({
          id: itemId,
          current: itemStatus === 'added' ? [] : ['title', 'description', 'textContent', 'visualTitle'],
          sync: itemStatus === 'added' ? ['title', 'description', 'textContent', 'visualTitle'] : [],
        }));

        updateSyncPageData({
          variables: {
            affectedResults: JSON.stringify(
              map(
                filter(checkedCurrentItems, ({ sync }) => sync.length),
                ({ id: itemId, sync }) => ({ [itemId]: sync }),
              ),
            ),
            overrideLabels,
          },
        });
        break;
      case 'MANUAL':
        onCloseConfirm();
        break;
      case 'DELETE':
        removeSyncData();
        break;
      default:
        console.warn(`Unexpected sync type: ${value}`);
        break;
    }
  };

  if (loadingClientState || loadingSyncPageData || loadingLastSyncDate) {
    return '';
  }

  if (!syncPageData.length) {
    return <Redirect to="/home/edit" />;
  }

  return (
    <div className={`sync-container ${status}`}>
      <div className="plan-diff-list">
        <PageList routeType="sync" />
      </div>
      <div className="plan-content">
        <div className="sync-table-outer">
          <div className="sync-table-inner">
            <table className="sync-table">
              <tbody>
                <tr>
                  <td className="sync-title" colSpan={2}>
                    <span>
                      <strong>Instruction:</strong>
                      Review and compare synced data on the right with your existing web content on the left on all pages. Select the data
                      you wish to use. Click Overwrite Data when all pages have been reviewed to replace existing web content.
                    </span>
                  </td>
                </tr>
                <tr className="sync-label">
                  <td className="current-label">{status !== 'removed' ? 'Current' : 'Removed'}</td>
                  <td className="new-label">New Sync</td>
                </tr>
                {isMeasure && (
                  <tr>
                    <td className={`current-content ${checkedData.current.includes('title') ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={includes(checkedData.current, 'title')}
                        data-type="title"
                        data-flag="current"
                        onChange={onCheck}
                        disabled={disabled}
                      />
                      <strong className="sync-label-section">Title</strong>
                      <div>{currentData.title}</div>
                    </td>
                    <td className={`sync-content ${checkedData.sync.includes('title') ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checkedData.sync.includes('title')}
                        data-type="title"
                        data-flag="sync"
                        onChange={onCheck}
                        disabled={disabled}
                      />
                      <strong className="sync-label-section">Title</strong>
                      <div>{syncData.title}</div>
                    </td>
                  </tr>
                )}
                <tr>
                  <td className={`current-content ${checkedData.current.includes('description') ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checkedData.current.includes('description')}
                      data-type="description"
                      data-flag="current"
                      onChange={onCheck}
                      disabled={disabled}
                    />
                    <strong className="sync-label-section">{`Description${isMeasure ? '' : ' (include start date/end date/tags/custom fields)'}`}</strong>
                    <div>{removeHtml(currentData.description)}</div>
                  </td>
                  <td className={`sync-content ${checkedData.sync.includes('description') ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checkedData.sync.includes('description')}
                      data-type="description"
                      data-flag="sync"
                      onChange={onCheck}
                      disabled={disabled}
                    />
                    <strong className="sync-label-section">{`Description${isMeasure ? '' : ' (include start date/end date/tags/custom fields)'}`}</strong>
                    <div>{removeHtml(syncData.description)}</div>
                  </td>
                </tr>
                <tr>
                  <td className={`current-content ${checkedData.current.includes('textContent') ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checkedData.current.includes('textContent')}
                      data-type="textContent"
                      data-flag="current"
                      onChange={onCheck}
                      disabled={disabled}
                    />
                    <strong className="sync-label-section">{`${textContentLabel}${isMeasure ? '' : ' (include status/progress/checklist)'}`}</strong>
                    <div>{removeHtml(currentData.textContent)}</div>
                  </td>
                  <td className={`sync-content ${includes(checkedData.sync, 'textContent') ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={includes(checkedData.sync, 'textContent')}
                      data-type="textContent"
                      data-flag="sync"
                      onChange={onCheck}
                      disabled={disabled}
                    />
                    <strong className="sync-label-section">{`${textContentLabel}${isMeasure ? '' : ' (include status/progress/checklist)'}`}</strong>
                    <div>{removeHtml(syncData.textContent)}</div>
                  </td>
                </tr>
                {isMeasure && (
                  <>
                    <tr>
                      <td className={`current-content ${includes(checkedData.current, 'visualTitle') ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={includes(checkedData.current, 'visualTitle')}
                          data-type="visualTitle"
                          data-flag="current"
                          onChange={onCheck}
                          disabled={disabled}
                        />
                        <strong className="sync-label-section">Visual Title</strong>
                        <div>{removeHtml(currentData.visualTitle)}</div>
                      </td>
                      <td className={`sync-content ${includes(checkedData.sync, 'visualTitle') ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={includes(checkedData.sync, 'visualTitle')}
                          data-type="visualTitle"
                          data-flag="sync"
                          onChange={onCheck}
                          disabled={disabled}
                        />
                        <strong className="sync-label-section">Visual Title</strong>
                        <div>{removeHtml(syncData.visualTitle)}</div>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {showPreConfirm && (
        <SyncPreConfirm
          onClose={onCloseConfirm}
          syncType={syncType}
          onConfirm={onConfirm}
          createdAt={createdAt}
          overrideLabels={overrideLabels}
          onToggleOverrideLabels={onToggleOverrideLabels}
          onSwitchSyncType={onSwitchSyncType}
          onTogglePublish={onTogglePublish}
          withPublish={withPublish}
        />
      )}
      {/* {showPreConfirm && (
        <div className="modal show saveSync">
          <div className="modal-dialog modal-lg lg">
            <div className="modal-content">
              <div className="modal-body">
                <div className="dangerous-popup">
                  <button type="button" className="close" onClick={onCloseConfirm}>
                    <span>×</span>
                  </button>
                  <div className="dangerous-popup-header medium">
                    <FontAwesomeIcon icon={['fal', 'exclamation-triangle']} />
                    Apply Synced Data from Plans
                  </div>
                  <form className="dangerous-popup-content" onSubmit={onConfirm}>
                    <p className="warning-text">
                      {`Data synced on ${moment(createdAt).format(
                        'MMM D, YYYY, h:mm:ss a',
                      )} from Plans has not been applied. Select one of the options below to apply your synced data.`}
                    </p>
                    <div className="dangerous-top-content">
                      <ul className="dangerous-top-content__list">
                        <li className="dangerous-top-content__list-item">
                          <label className="dangerous-top-content__label">
                            <input
                              type="radio"
                              name="syncType"
                              value="CURRENT"
                              checked={syncType === 'CURRENT'}
                              onChange={onSwitchSyncType}
                              className="dangerous-top-content__radio"
                            />
                            <span>Retain descriptions and only replace plan element updates with data from Plans.</span>
                          </label>
                        </li>
                        <li className="dangerous-top-content__list-item">
                          <label className="dangerous-top-content__label">
                            <input
                              type="radio"
                              name="syncType"
                              value="INCOMING"
                              checked={syncType === 'INCOMING'}
                              onChange={onSwitchSyncType}
                              className="dangerous-top-content__radio"
                            />
                            <span>Replace all plan element descriptions and updates with data from Plans.</span>
                          </label>
                          {syncType === 'INCOMING' && (
                            <label className="dangerous-top-content__label">
                              <input
                                type="checkbox"
                                name="overrideLabels"
                                checked={overrideLabels}
                                onChange={onToggleOverrideLabels}
                                className="dangerous-top-content__radio"
                              />
                              <span>Override navigation labels with plan element descriptions</span>
                            </label>
                          )}
                        </li>
                        <li className="dangerous-top-content__list-item">
                          <label className="dangerous-top-content__label">
                            <input
                              type="radio"
                              name="syncType"
                              value="MANUAL"
                              checked={syncType === 'MANUAL'}
                              onChange={onSwitchSyncType}
                              className="dangerous-top-content__radio"
                            />
                            <span>Choose specific plan elements for application.</span>
                          </label>
                        </li>
                        <li className="dangerous-top-content__list-item">
                          <label className="dangerous-top-content__label">
                            <input
                              type="radio"
                              name="syncType"
                              value="DELETE"
                              checked={syncType === 'DELETE'}
                              onChange={onSwitchSyncType}
                              className="dangerous-top-content__radio"
                            />
                            <span>Exit and delete sync stays.</span>
                          </label>
                        </li>
                      </ul>
                    </div>
                    {['CURRENT', 'INCOMING'].includes(syncType) && (
                      <div className="form-group dangerous-top-content__form-group">
                        <label className="dangerous-top-content__toggle">
                          <Toggle checked={withPublish} onChange={onTogglePublish} />
                          <span className="dangerous-top-content__toggle-text">Publish</span>
                        </label>
                      </div>
                    )}
                    {['INCOMING', 'CURRENT'].includes(syncType) && withPublish && (
                      <p className="warning-text">
                        All changes will be published and made accessible to the public. This action cannot be reversed. Are you sure?
                      </p>
                    )}
                    {['INCOMING', 'CURRENT'].includes(syncType) && (
                      <div className="field-group">
                        <p>Type OVERWRITE to continue.</p>
                        <div className="dangerous-middle-content">
                          <input className="form-control" pattern="OVERWRITE" placeholder="Type here..." required />
                        </div>
                      </div>
                    )}
                    {syncType === 'DELETE' && (
                      <div className="field-group">
                        <p>Type DELETE to continue: </p>
                        <div className="dangerous-middle-content">
                          <input className="form-control" pattern="DELETE" placeholder="Type here..." required />
                        </div>
                      </div>
                    )}
                    <div className="button-container">
                      <button type="button" className="btn mint-btn dangerous-popup-btn" onClick={onCloseConfirm}>
                        Cancel
                      </button>
                      <button type="submit" className="btn mint-btn dangerous-popup-btn">
                        Confirm
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
}
